import { useQuery } from '@tanstack/react-query';
import { RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { fallbackPrices } from '@/data/pricing/fallbackPrices';

const PRICING_API_URL =
  'https://seniorthrive-backend-us-471216032729.us-central1.run.app/api/stripe/public/pricing';

interface StripePlan {
  planId: string;
  name: string;
  description: string | null;
  isBasePlan: boolean;
  isAddon: boolean;
  priceCurrency: string | null;
  monthlyPriceAmount: number | null;
  annualPriceAmount: number | null;
  stripeProductId: string | null;
  stripePriceMonthlyId: string | null;
  stripePriceAnnualId: string | null;
}

interface PricingApiResponse {
  plans: StripePlan[];
  version: string;
  generatedAt: string;
}

const nameToKey: Record<string, string> = {
  ThriveMax: 'thrivemax',
  ThriveFamily: 'thrivefamily',
  'ThriveFamily Plus': 'thrivefamily-plus',
  'ThriveCare Add-On': 'thrivecare-addon',
};

const AdminPricing = () => {
  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useQuery<PricingApiResponse>({
    queryKey: ['admin-stripe-pricing'],
    queryFn: async () => {
      const res = await fetch(PRICING_API_URL);
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      return res.json();
    },
    staleTime: 60 * 1000,
  });

  const mismatches: string[] = [];
  if (data) {
    for (const plan of data.plans) {
      const key = nameToKey[plan.name];
      if (!key) continue;
      const fb = fallbackPrices[key];
      if (!fb) continue;
      if (plan.monthlyPriceAmount != null && plan.monthlyPriceAmount !== fb.monthlyPrice) {
        mismatches.push(`${plan.name} monthly: Stripe $${plan.monthlyPriceAmount} vs Frontend $${fb.monthlyPrice}`);
      }
      if (plan.annualPriceAmount != null && fb.annualPrice != null && plan.annualPriceAmount !== fb.annualPrice) {
        mismatches.push(`${plan.name} annual: Stripe $${plan.annualPriceAmount} vs Frontend $${fb.annualPrice}`);
      }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Pricing Manager</h1>
          <p className="text-sm text-muted-foreground">
            Real-time Stripe pricing data vs. frontend fallback values
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {mismatches.length > 0 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-destructive text-sm">
                  {mismatches.length} price{mismatches.length > 1 ? 's' : ''} out of sync with Stripe
                </p>
                <ul className="mt-1 space-y-0.5">
                  {mismatches.map((m, i) => (
                    <li key={i} className="text-xs text-muted-foreground">{m}</li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  Update <code className="bg-muted px-1 rounded">src/data/pricing/fallbackPrices.ts</code> to resolve.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {mismatches.length === 0 && data && (
        <Card className="border-green-500/30 bg-green-50/50 dark:bg-green-950/10">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700 dark:text-green-400 font-medium">
              All tracked prices are in sync with Stripe.
            </p>
          </CardContent>
        </Card>
      )}

      {isError && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <p className="text-sm text-destructive">
              Failed to fetch pricing: {(error as Error)?.message}
            </p>
          </CardContent>
        </Card>
      )}

      {dataUpdatedAt > 0 && (
        <p className="text-xs text-muted-foreground">
          Last fetched: {new Date(dataUpdatedAt).toLocaleString()}
          {data?.generatedAt && ` · API timestamp: ${new Date(data.generatedAt).toLocaleString()}`}
        </p>
      )}

      {isLoading && !data ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="pt-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
          ))}
        </div>
      ) : data ? (
        <div className="grid md:grid-cols-2 gap-4">
          {data.plans.map((plan) => {
            const key = nameToKey[plan.name];
            const fb = key ? fallbackPrices[key] : null;

            const monthlyMatch = !fb || plan.monthlyPriceAmount == null || plan.monthlyPriceAmount === fb.monthlyPrice;
            const annualMatch = !fb || plan.annualPriceAmount == null || !fb.annualPrice || plan.annualPriceAmount === fb.annualPrice;

            return (
              <Card key={plan.planId} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    <div className="flex gap-1.5">
                      {plan.isBasePlan && <Badge variant="secondary" className="text-xs">Base</Badge>}
                      {plan.isAddon && <Badge variant="outline" className="text-xs">Add-On</Badge>}
                    </div>
                  </div>
                  {plan.description && (
                    <p className="text-xs text-muted-foreground">{plan.description}</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {plan.monthlyPriceAmount != null && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Monthly</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${!monthlyMatch ? 'text-destructive' : 'text-foreground'}`}>
                          ${plan.monthlyPriceAmount}/mo
                        </span>
                        {fb && !monthlyMatch && (
                          <span className="text-xs text-muted-foreground line-through">
                            Frontend: ${fb.monthlyPrice}
                          </span>
                        )}
                        {fb && monthlyMatch && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                        )}
                      </div>
                    </div>
                  )}

                  {plan.annualPriceAmount != null && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Annual</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${!annualMatch ? 'text-destructive' : 'text-foreground'}`}>
                          ${plan.annualPriceAmount}/yr
                        </span>
                        {fb && fb.annualPrice && !annualMatch && (
                          <span className="text-xs text-muted-foreground line-through">
                            Frontend: ${fb.annualPrice}
                          </span>
                        )}
                        {fb && annualMatch && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                        )}
                      </div>
                    </div>
                  )}

                  <div className="pt-2 border-t border-border/50 space-y-1">
                    {plan.stripeProductId && (
                      <p className="text-[10px] text-muted-foreground/70 font-mono">
                        Product: {plan.stripeProductId}
                      </p>
                    )}
                    {plan.stripePriceMonthlyId && (
                      <p className="text-[10px] text-muted-foreground/70 font-mono">
                        Monthly Price: {plan.stripePriceMonthlyId}
                      </p>
                    )}
                    {plan.stripePriceAnnualId && (
                      <p className="text-[10px] text-muted-foreground/70 font-mono">
                        Annual Price: {plan.stripePriceAnnualId}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default AdminPricing;
